import { z } from 'zod';
import { MAX_OFFER_AMOUNT_CENTS, MIN_OFFER_AMOUNT_CENTS } from '../constants.js';
import { LEAD_TYPES, OFFER_STATUSES, PAYMENT_FREQUENCIES } from '../enums.js';
import { longText, paginationSchema, phoneSchema, shortText, uuid } from './common.js';

/**
 * Money arrives as a decimal string or number in euros and is stored as an
 * integer number of cents. The legacy column was `varchar(255)`, which meant
 * `ORDER BY amount ASC` sorted quotes lexically — "1000" ahead of "9".
 */
export const euroAmountToCents = z
  .union([z.string(), z.number()])
  .transform((value, ctx) => {
    const raw = typeof value === 'number' ? value.toString() : value.trim().replace(',', '.');
    if (!/^\d{1,7}(\.\d{1,2})?$/.test(raw)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Ungültiger Betrag' });
      return z.NEVER;
    }
    const cents = Math.round(Number.parseFloat(raw) * 100);
    if (cents < MIN_OFFER_AMOUNT_CENTS || cents > MAX_OFFER_AMOUNT_CENTS) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Betrag außerhalb des zulässigen Bereichs' });
      return z.NEVER;
    }
    return cents;
  });

/** Partner sends a quote for one assigned lead. */
export const createOfferSchema = z.object({
  leadId: uuid,
  amount: euroAmountToCents,
  paymentFrequency: z.enum(PAYMENT_FREQUENCIES),
  title: shortText(160),
  body: longText(4000),
  documentAssetId: uuid.optional(),
});

export const updateOfferSchema = createOfferSchema.partial().omit({ leadId: true });

/**
 * Accepting an offer reveals the consumer's phone number to the winning
 * partner. The legacy endpoint took `userid` from the body and wrote the phone
 * number onto every lead of that type for that user.
 */
export const acceptOfferSchema = z.object({
  contactPhone: phoneSchema,
});

export const markOffersSeenSchema = z.object({
  offerIds: z.array(uuid).min(1).max(200),
});

export const offerListQuerySchema = paginationSchema.extend({
  leadType: z.enum(LEAD_TYPES).optional(),
  status: z.enum(OFFER_STATUSES).optional(),
  sort: z.enum(['amount', 'createdAt']).default('amount'),
  direction: z.enum(['asc', 'desc']).default('asc'),
});

export type CreateOfferInput = z.infer<typeof createOfferSchema>;
export type AcceptOfferInput = z.infer<typeof acceptOfferSchema>;
